import { createContext, useContext, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useOrgPath } from '../app/paths';
import { cn } from '../lib/cn';

/**
 * A name that goes somewhere.
 *
 * Anything on a screen that names another thing in the workspace — a
 * person, a client, a project, a task, an invoice, a quote — is a door to
 * it, not a label. One component so every such door looks the same and
 * behaves the same: a real anchor (middle-click, cmd-click and hover all
 * work), org-scoped, and safe inside a row that navigates on click, because
 * the click stops here rather than opening the row's target as well.
 */
export function EntityLink({
  to,
  children,
  tone = 'ink',
  className,
  title,
}: {
  /** An org-relative path: `/projects/abc`, `/team/uid`. */
  to: string;
  children: ReactNode;
  /** `ink` for the primary name in a row; `muted` for a secondary one. */
  tone?: 'ink' | 'muted';
  className?: string;
  title?: string;
}) {
  const orgPath = useOrgPath();
  const canOpen = useContext(PathVisibility);
  if (!canOpen(to)) {
    return (
      <span title={title} className={cn(tone === 'muted' ? 'text-muted' : 'text-ink', className)}>
        {children}
      </span>
    );
  }
  return (
    <Link
      to={orgPath(to)}
      title={title}
      onClick={(event: MouseEvent) => event.stopPropagation()}
      className={cn(
        'rounded-[2px] underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ink/30',
        tone === 'muted' ? 'text-muted hover:text-ink' : 'text-ink',
        className,
      )}
    >
      {children}
    </Link>
  );
}

/**
 * Which org-relative paths this person can open. Everything, unless the app
 * shell says otherwise — it provides the answer from the workspace's
 * switched-off areas, once for the whole screen.
 */
export const PathVisibility = createContext<(path: string) => boolean>(() => true);

/** The org-relative path for each kind of thing, in one place. */
export const entityPath = {
  person: (uid: string) => `/team/${uid}`,
  client: (clientId: string) => `/clients/${clientId}`,
  project: (projectId: string) => `/projects/${projectId}`,
  task: (taskId: string) => `/tasks/${taskId}`,
  invoice: (invoiceId: string) => `/finance/invoices/${invoiceId}`,
  quote: (quoteId: string) => `/finance/quotes/${quoteId}`,
  leaveRequest: (requestId: string) => `/leave/requests/${requestId}`,
};
